import { jwtDecode } from "@Front/Utils/jwtDecodeWrapper";
import {
	getLeCoffreRefreshTokenCookie,
	getLeCoffreCustomerAccessTokenCookie,
	getLeCoffreCustomerRefreshTokenCookie,
	getLeCoffreThirdPartyAccessTokenCookie,
	getLeCoffreThirdPartyRefreshTokenCookie,
	LECOFFRE_NOTARY_ACCESS_TOKEN_COOKIE_NAME as LECOFFRE_ACCESS_TOKEN_COOKIE_NAME,
	LECOFFRE_NOTARY_REFRESH_TOKEN_COOKIE_NAME as LECOFFRE_REFRESH_TOKEN_COOKIE_NAME,
} from "@Front/Utils/lecoffreSessionCookieHelpers";
import { resolveActiveAccessTokenFromSession, resolveActiveRefreshTokenFromSession } from "@Front/Api/helpers/authTokenResolutionHelpers";

export {
	getLeCoffreRefreshTokenCookie,
	LECOFFRE_ACCESS_TOKEN_COOKIE_NAME,
	LECOFFRE_REFRESH_TOKEN_COOKIE_NAME,
};

type JwtPayloadWithUserKeys = {
	userId?: string;
	customerId?: string;
	uid?: string;
	sub?: string;
	customer_uid?: string;
	user_id?: string;
};
type JwtPayloadWithFolderUid = { folderUid?: string };
type JwtPayloadWithEmailKeys = { email?: string; mail?: string; preferred_email?: string };

const JWT_SUBJECT_CLAIM_KEYS: Array<keyof JwtPayloadWithUserKeys> = [
	"userId",
	"customerId",
	"uid",
	"sub",
	"customer_uid",
	"user_id",
];
const JWT_EMAIL_CLAIM_KEYS: Array<keyof JwtPayloadWithEmailKeys> = ["email", "mail", "preferred_email"];

function readFirstNonEmptyStringClaim(
	payload: Record<string, unknown> | null | undefined,
	keys: string[],
): string | null {
	if (!payload) {
		return null;
	}
	for (const key of keys) {
		const candidate = payload[key];
		if (typeof candidate === "string" && candidate.length > 0) {
			return candidate;
		}
	}
	return null;
}

/** `userId` or legacy `uid` claim from an access token string. */
export function decodeLeCoffreAccessTokenUserUid(accessToken: string): string | null {
	if (!accessToken) {
		return null;
	}
	try {
		const payload = jwtDecode<JwtPayloadWithUserKeys>(accessToken);
		const id = payload?.userId ?? payload?.uid;
		return typeof id === "string" && id.length > 0 ? id : null;
	} catch {
		return null;
	}
}

/** Subject uid (`userId`, `customerId`, or legacy `uid`) from an access token string. */
export function decodeLeCoffreAccessTokenSubjectUid(accessToken: string): string | null {
	if (!accessToken) {
		return null;
	}
	try {
		const payload = jwtDecode<JwtPayloadWithUserKeys>(accessToken);
		return readFirstNonEmptyStringClaim(payload as Record<string, unknown>, JWT_SUBJECT_CLAIM_KEYS);
	} catch {
		return null;
	}
}

/** Subject uid (`userId`, `customerId`, or legacy `uid`) from a refresh token string. */
export function decodeLeCoffreRefreshTokenSubjectUid(refreshToken: string): string | null {
	if (!refreshToken) {
		return null;
	}
	try {
		const payload = jwtDecode<JwtPayloadWithUserKeys>(refreshToken);
		return readFirstNonEmptyStringClaim(payload as Record<string, unknown>, JWT_SUBJECT_CLAIM_KEYS);
	} catch {
		return null;
	}
}

/** Email claim from an access token string. */
export function decodeLeCoffreAccessTokenEmail(accessToken: string): string | null {
	if (!accessToken) {
		return null;
	}
	try {
		const payload = jwtDecode<JwtPayloadWithEmailKeys>(accessToken);
		return readFirstNonEmptyStringClaim(payload as Record<string, unknown>, JWT_EMAIL_CLAIM_KEYS);
	} catch {
		return null;
	}
}

/** Email claim from a refresh token string. */
export function decodeLeCoffreRefreshTokenEmail(refreshToken: string): string | null {
	if (!refreshToken) {
		return null;
	}
	try {
		const payload = jwtDecode<JwtPayloadWithEmailKeys>(refreshToken);
		return readFirstNonEmptyStringClaim(payload as Record<string, unknown>, JWT_EMAIL_CLAIM_KEYS);
	} catch {
		return null;
	}
}


/** `folderUid` claim (tiers / contexte dossier) from an access token string. */
export function decodeLeCoffreAccessTokenFolderUid(accessToken: string): string | null {
	if (!accessToken) {
		return null;
	}
	try {
		const payload = jwtDecode<JwtPayloadWithFolderUid>(accessToken);
		const folderUid = payload?.folderUid ?? null;
		return typeof folderUid === "string" && folderUid.length > 0 ? folderUid : null;
	} catch {
		return null;
	}
}

/** User uid from the LeCoffre access token cookie, when present. */
export function getLeCoffreAccessTokenUserUid(): string | null {
	const token = resolveActiveAccessTokenFromSession();
	return token ? decodeLeCoffreAccessTokenUserUid(token) : null;
}

/** Subject uid (`userId`/`customerId`) from active LeCoffre access token cookie, when present. */
export function getLeCoffreAccessTokenSubjectUid(): string | null {
	const token = resolveActiveAccessTokenFromSession();
	return token ? decodeLeCoffreAccessTokenSubjectUid(token) : null;
}

/** Subject uid (`userId`/`customerId`) from active LeCoffre refresh token cookie, when present. */
export function getLeCoffreRefreshTokenSubjectUid(): string | null {
	const token = resolveActiveRefreshTokenFromSession();
	return token ? decodeLeCoffreRefreshTokenSubjectUid(token) : null;
}

/** Subject uid (`userId`/`customerId`) from customer refresh token cookie, when present. */
export function getLeCoffreCustomerRefreshTokenSubjectUid(): string | null {
	const token = getLeCoffreCustomerRefreshTokenCookie();
	return token ? decodeLeCoffreRefreshTokenSubjectUid(token) : null;
}

/** Email from customer access token cookie, for customer session identity flows. */
export function getLeCoffreCustomerAccessTokenEmail(): string | null {
	const token = getLeCoffreCustomerAccessTokenCookie();
	return token ? decodeLeCoffreAccessTokenEmail(token) : null;
}

/** Email from customer refresh token cookie, for pre-refresh identity resolution. */
export function getLeCoffreCustomerRefreshTokenEmail(): string | null {
	const token = getLeCoffreCustomerRefreshTokenCookie();
	return token ? decodeLeCoffreRefreshTokenEmail(token) : null;
}

/** Email from active session access token (store/cookie selected context). */
export function getLeCoffreActiveSessionAccessTokenEmail(): string | null {
	const token = resolveActiveAccessTokenFromSession();
	return token ? decodeLeCoffreAccessTokenEmail(token) : null;
}

/** Email from active session refresh token cookie (selected context). */
export function getLeCoffreActiveSessionRefreshTokenEmail(): string | null {
	const token = resolveActiveRefreshTokenFromSession();
	return token ? decodeLeCoffreRefreshTokenEmail(token) : null;
}

/** Subject uid (`userId`/`customerId`) from third-party access token cookie, when present. */
export function getLeCoffreThirdPartyAccessTokenSubjectUid(): string | null {
	const token = getLeCoffreThirdPartyAccessTokenCookie();
	return token ? decodeLeCoffreAccessTokenSubjectUid(token) : null;
}

/** Subject uid (`userId`/`customerId`) from third-party refresh token cookie, when present. */
export function getLeCoffreThirdPartyRefreshTokenSubjectUid(): string | null {
	const token = getLeCoffreThirdPartyRefreshTokenCookie();
	return token ? decodeLeCoffreRefreshTokenSubjectUid(token) : null;
}

/** Email from third-party access token cookie. */
export function getLeCoffreThirdPartyAccessTokenEmail(): string | null {
	const token = getLeCoffreThirdPartyAccessTokenCookie();
	return token ? decodeLeCoffreAccessTokenEmail(token) : null;
}

/** Email from third-party refresh token cookie. */
export function getLeCoffreThirdPartyRefreshTokenEmail(): string | null {
	const token = getLeCoffreThirdPartyRefreshTokenCookie();
	return token ? decodeLeCoffreRefreshTokenEmail(token) : null;
}

